import { Menu } from 'lucide-react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useOpenSession, useStatistics } from '../hooks/useData';
import { formatTimer } from '../utils/core';
import { readingService } from '../services/readingService';
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { UserAvatar } from '../components/UserAvatar';
import { getFirstName } from '../services/profileService';
import { AppMenu } from './AppMenu';
import { DesktopSidebar } from './DesktopSidebar';
import { primaryNavigation } from './navigation';
import { getReadingLevel } from '../domain/readingLevel';
import { LevelUpNotice } from '../components/feedback/LevelUpNotice';

export const AppShell = () => {
  const location = useLocation();
  const auth = useAuth();
  const openSession = useOpenSession();
  const totalStatistics = useStatistics('total');
  const readingLevel = getReadingLevel(totalStatistics?.durationMs ?? 0);
  const [elapsed, setElapsed] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const focused = location.pathname.startsWith('/session/');
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  useEffect(() => {
    if (!openSession) return;
    const tick = () => setElapsed(readingService.elapsedMs(openSession));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [openSession]);

  return (
    <div className={`app-shell ${focused ? 'app-shell--focused' : ''}`}>
      {!focused && (
        <DesktopSidebar
          firstName={getFirstName(auth.user)}
          level={readingLevel.currentLevel}
          openSession={openSession}
          elapsed={formatTimer(elapsed)}
          onSignOut={auth.signOut}
        />
      )}
      <div className="app-shell__workspace">
      {!focused && (
        <header className="user-topbar">
          <div className="user-topbar__inner">
            <NavLink to="/profile" className="user-topbar__profile" aria-label="Abrir perfil">
              <UserAvatar />
              <span className="user-topbar__identity">
                <span>{getFirstName(auth.user)}</span>
                <small>Level {readingLevel.currentLevel}</small>
              </span>
            </NavLink>
            <button type="button" className="user-topbar__menu" aria-label="Abrir menu" onClick={() => setMenuOpen(true)}>
              <Menu aria-hidden="true" />
            </button>
          </div>
        </header>
      )}
      {openSession && !focused && (
        <NavLink to={`/session/${openSession.id}`} className="active-session-pill">
          <span className="pulse-dot" />
          <span>{openSession.status === 'paused' ? 'Sessão pausada' : 'Lendo agora'}</span>
          <strong>{formatTimer(elapsed)}</strong>
        </NavLink>
      )}
      <main className="app-main"><Outlet /></main>
      {!focused && (
        <>
          <nav className="bottom-nav" aria-label="Navegação principal">
            {primaryNavigation.map(({ to, label, icon: Icon, central, end }) => (
              <NavLink key={to} to={to} {...(end ? { end: true } : {})} className={({ isActive }) => `bottom-nav__item ${central ? 'bottom-nav__item--add' : ''} ${isActive ? 'is-active' : ''}`}>
                <span><Icon aria-hidden="true" /></span><small>{label}</small>
              </NavLink>
            ))}
          </nav>
          <AppMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
        </>
      )}
      <LevelUpNotice />
      </div>
    </div>
  );
};
